import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { FaWhatsapp, FaArrowLeft, FaInfoCircle, FaShoppingCart } from 'react-icons/fa';
import { useCart } from '../context/CartContext';
import './DetalleProducto.css';

const DetalleProducto = () => {
  const { id } = useParams();
  const { agregarAlCarrito, setIsCartOpen } = useCart();
  
  // Estados para los datos que vienen de PostgreSQL
  const [producto, setProducto] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [errorFetch, setErrorFetch] = useState(null);

  // Estados para la selección del usuario
  const [presentacionElegida, setPresentacionElegida] = useState(null);
  const [maduracionElegida, setMaduracionElegida] = useState(null);
  const [cantidad, setCantidad] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const cargarProducto = async () => {
      try {
        const respuesta = await fetch(`https://api.pascolo.com.ar/api/web/productos/${id}`);
        if (!respuesta.ok) throw new Error("Producto no encontrado");
        
        const data = await respuesta.json();
        setProducto(data);
        
        // Extraemos las presentaciones únicas para el primer render
        const formatosUnicos = extraerFormatosUnicos(data.variantes);
        
        if (formatosUnicos.length > 0) {
          setPresentacionElegida(formatosUnicos[0]); // Seleccionamos la primera por defecto
        }
        
        setCargando(false);
      } catch (error) {
        setErrorFetch(error.message);
        setCargando(false);
      }
    };

    cargarProducto();
  }, [id]);

  // Funciones auxiliares para filtrar los datos del backend
  const extraerFormatosUnicos = (variantes) => {
    const mapa = new Map();
    variantes.forEach(v => {
      if (!mapa.has(v.presentacion_id)) {
        mapa.set(v.presentacion_id, {
          id: v.presentacion_id,
          formato: v.formato,
          peso_estimado: parseFloat(v.peso_estimado_kg)
        });
      }
    });
    return Array.from(mapa.values());
  };

  // Cada vez que cambia la presentación, reseteamos la maduración
  useEffect(() => {
    if (presentacionElegida && producto) {
      const maduracionesParaEsteFormato = producto.variantes.filter(
        v => v.presentacion_id === presentacionElegida.id
      );
      if (maduracionesParaEsteFormato.length > 0) {
        // Seleccionamos la primera maduración disponible de ese formato
        setMaduracionElegida(maduracionesParaEsteFormato[0]);
        setCantidad(1); // Reseteamos cantidad al cambiar
      } else {
        setMaduracionElegida(null);
      }
    }
  }, [presentacionElegida, producto]);

  if (cargando) return <div className="cargando">Cargando detalles del queso...</div>;
  if (errorFetch || !producto) {
    return (
      <div className="producto-no-encontrado">
        <h2>Producto no encontrado</h2>
        <Link to="/productos" className="btn-volver">Volver al catálogo</Link>
      </div>
    );
  }

  // --- LÓGICA DE STOCK Y PRECIO ---
  const maduracionesDisponibles = presentacionElegida 
    ? producto.variantes.filter(v => v.presentacion_id === presentacionElegida.id)
    : [];

  const stockActual = maduracionElegida ? maduracionElegida.stock_disponible : 0;
  const hayStock = stockActual > 0 && cantidad <= stockActual;
  
  // El precio se calcula: (Precio Base por Kg) x (Peso Estimado de la Presentación)
  const precioCalculado = presentacionElegida 
    ? parseFloat(producto.precio_base_kg) * presentacionElegida.peso_estimado
    : 0;

  const formatearPrecio = (precio) => {
    return new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(precio);
  };

 const handleAgregarAlCarrito = () => {
    if (!hayStock) return;
    
    const itemCarrito = {
      producto_id: producto.id,
      nombre: producto.nombre,
      presentacion_id: presentacionElegida.id,
      formato: presentacionElegida.formato,
      peso_estimado: presentacionElegida.peso_estimado, // Útil por si querés mostrar el peso total del pedido
      maduracion: maduracionElegida.dias_maduracion,
      cantidad: cantidad,
      precio_unitario: precioCalculado,
      imagen_url: producto.imagen_url, // Clave para mostrar la fotito en la pantalla del carrito
      stock_maximo: stockActual
    };
    
    // Lo disparamos al Contexto
    agregarAlCarrito(itemCarrito);
    
    // Alerta temporal (podés cambiarlo por un toast/snackbar copado más adelante)
    setIsCartOpen(true); 
  };

  return (
    <div className="detalle-container">
      <Link to="/productos" className="btn-volver"><FaArrowLeft /> Volver al catálogo</Link>

      <div className="detalle-content">
        <div className="detalle-imagen-izq">
          <img 
            src={producto.imagen_url || '/placeholder-queso.jpg'} 
            alt={producto.nombre} 
            className="card-image" 
            loading="lazy" 
          />
          <div className="nota-aclaratoria">
            <p><FaInfoCircle /> <strong>¡Información sobre el peso!</strong><br/>
            El peso real del queso puede diferir ligeramente. Nos aseguramos de que el valor sea justo y proporcional al peso real.</p>
          </div>
        </div>
        
        <div className="detalle-info-der">
          <h1>{producto.nombre}</h1>
          
          <div className="precio-destacado">
            {precioCalculado > 0 ? formatearPrecio(precioCalculado) : 'Consultar precio'}
            {presentacionElegida && (
              <span style={{fontSize: '14px', color: '#666', display: 'block', fontWeight: 'normal'}}>
                Peso real aprox. {presentacionElegida.peso_estimado} kg
              </span>
            )}
          </div>

          <p className="detalle-descripcion">{producto.descripcion}</p>
          
          {/* 1. SELECTOR DE FORMATOS (Presentación) */}
          <div className="selector-tamanos">
            <h3>1. Elegí el formato:</h3>
            <div className="opciones-grid">
              {extraerFormatosUnicos(producto.variantes).map((formato) => (
                <div 
                  key={formato.id}
                  className={`opcion-card ${presentacionElegida?.id === formato.id ? 'seleccionado' : ''}`}
                  onClick={() => setPresentacionElegida(formato)}
                >
                  <span className="opcion-nombre">{formato.formato}</span>
                </div>
              ))}
            </div>
          </div>

          {/* 2. SELECTOR DE MADURACIÓN (Depende del formato) */}
          {presentacionElegida && maduracionesDisponibles.length > 0 && (
            <div className="selector-tamanos" style={{ marginTop: '20px' }}>
              <h3>2. Maduración:</h3>
              <div className="opciones-grid">
                {maduracionesDisponibles.map((varMaduracion, index) => {
                  const sinStock = varMaduracion.stock_disponible <= 0;
                  return (
                    <div 
                      key={index}
                      className={`opcion-card 
                        ${maduracionElegida?.dias_maduracion === varMaduracion.dias_maduracion ? 'seleccionado' : ''} 
                        ${sinStock ? 'sin-stock' : ''}`
                      }
                      onClick={() => !sinStock && setMaduracionElegida(varMaduracion)}
                      style={{ opacity: sinStock ? 0.5 : 1, cursor: sinStock ? 'not-allowed' : 'pointer' }}
                    >
                      <span className="opcion-nombre">
                        {varMaduracion.dias_maduracion === 0 ? 'Fresco' : `${varMaduracion.dias_maduracion} días`}
                      </span>
                      <span className="opcion-peso">
                        {sinStock ? '(Agotado)' : `(Stock: ${varMaduracion.stock_disponible})`}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* CONTROLES DE COMPRA */}
          <div className="controles-compra" style={{ marginTop: '25px', display: 'flex', gap: '15px', alignItems: 'center' }}>
            <div className="contador-cantidad" style={{ display: 'flex', alignItems: 'center', border: '1px solid #ccc', borderRadius: '5px' }}>
              <button 
                onClick={() => setCantidad(Math.max(1, cantidad - 1))}
                style={{ padding: '10px 15px', background: 'transparent', border: 'none', cursor: 'pointer' }}
              >-</button>
              <span style={{ padding: '0 15px', fontWeight: 'bold' }}>{cantidad}</span>
              <button 
                onClick={() => setCantidad(Math.min(stockActual, cantidad + 1))}
                disabled={cantidad >= stockActual}
                style={{ padding: '10px 15px', background: 'transparent', border: 'none', cursor: 'pointer' }}
              >+</button>
            </div>

            <button 
              className={`btn-comprar-wa grande ${!hayStock ? 'disabled' : ''}`} 
              onClick={handleAgregarAlCarrito}
              disabled={!hayStock}
              style={{ background: hayStock ? '#2d5a27' : '#999', color: 'white', flex: 1, border: 'none' }}
            >
              <FaShoppingCart style={{ marginRight: '8px' }} /> 
              {hayStock ? 'Añadir al Carrito' : 'Sin Stock Disponible'}
            </button>
          </div>

          <p style={{ marginTop: '20px', fontSize: '13px', color: '#666' }}>
            <FaInfoCircle /> <strong>Envíos:</strong> Los productos no cuentan con envío. Para la información del retiro contacte vía WhatsApp.
          </p>
        </div>
      </div>
    </div>
  );
};

export default DetalleProducto;